/**
 * ehr#172 / ehr#364 - Log out: end the shared SSO session (the EHR clears the parent-domain cookie
 * itself; the login service is also told, best effort), drop any "view as" impersonation, then go
 * to the Credify sign-in page, which returns here after. Used by the menu's Log out and by the
 * "Your sign-in has expired" window's Sign out. Each step is best effort: with an expired session
 * the EHR's own logout answers 401, and the person still ends up at sign-in.
 */
export interface SignOutDeps {
  postLogout: () => Promise<unknown>; // the EHR's POST /auth/logout
  fetchFn: typeof fetch;
  storage: { local?: Pick<Storage, 'removeItem'>; session?: Pick<Storage, 'removeItem'> };
  assign: (url: string) => void;
  isProd: boolean;
  origin: string;
}

export const LOGIN_LOGOUT_URL = 'https://login.credifyfast.com/api/auth/logout';

export function signInUrlAfterLogout(isProd: boolean, origin: string): string {
  return isProd
    ? `https://login.credifyfast.com/login?redirect=${encodeURIComponent(`${origin}/`)}`
    : '/';
}

/** ehr#416: after an auto sign-out, Sign In Again comes back to the same page. */
export function signInAgainUrl(isProd: boolean, href: string): string {
  return isProd ? `https://login.credifyfast.com/login?redirect=${encodeURIComponent(href)}` : '/';
}

export async function signOutEverywhere(d: SignOutDeps): Promise<void> {
  await endSession(d);
  d.assign(signInUrlAfterLogout(d.isProd, d.origin));
}

/** End the shared session and drop "view as", staying on the page (ehr#416's signed-out screen). */
export async function endSession(d: Omit<SignOutDeps, 'assign' | 'isProd' | 'origin'>) {
  try {
    await d.postLogout();
  } catch {
    /* the person still ends up at sign-in */
  }
  try {
    await d.fetchFn(LOGIN_LOGOUT_URL, {
      method: 'POST',
      credentials: 'include',
      mode: 'no-cors',
      keepalive: true,
    });
  } catch {
    /* best effort */
  }
  try {
    d.storage.local?.removeItem('impersonate');
    d.storage.session?.removeItem('ehr.sso.redirected');
  } catch {
    /* storage blocked */
  }
}
